"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { FileText, Cpu, Boxes, FlaskConical, ShieldCheck, ArrowRight, CircleCheck as CheckCircle2, Sparkles } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const sections = [{
  number: "01",
  title: "Overview",
  icon: FileText,
  badge: "Manufacturer Profile",
  description: "Company heritage, manufacturing credentials, compliance certifications, and global quality benchmarks.",
  keyPoints: ["Principal profile and pedigree", "ISO & GMP manufacturing standards", "Regulatory documentation library"]
}, {
  number: "02",
  title: "Technologies",
  icon: Cpu,
  badge: "Core Engineering",
  description: "Deep dive into analytical detection principles, optical designs, and patent-protected measurement architectures.",
  keyPoints: ["Patented analytical mechanisms", "Software and data integrity tools", "Automation and throughput capabilities"]
}, {
  number: "03",
  title: "Products",
  icon: Boxes,
  badge: "Hardware & Consumables",
  description: "Comprehensive catalogue of primary analytical instruments, modular accessories, detector options, and consumables.",
  keyPoints: ["Benchtop and in-line systems", "Modular upgrades and sample handlers", "Certified reference standards"]
}, {
  number: "04",
  title: "Applications",
  icon: FlaskConical,
  badge: "Industry Solutions",
  description: "Proven test protocols, industry-specific analytical methods, and validated sample preparation procedures.",
  keyPoints: ["Pharmaceutical and biopharma methods", "Food, beverage, and water analysis", "Industrial QA/QC testing workflows"]
}, {
  number: "05",
  title: "Antech Services",
  icon: ShieldCheck,
  badge: "Lifecycle Support",
  description: "Dedicated local support from Antech Solutions including installation, IQ/OQ qualification, and preventative maintenance.",
  keyPoints: ["Accredited IQ/OQ/PQ validation", "Rapid on-site response in Ireland & UK", "Preventative maintenance agreements"]
}];
export default function SupplierBreakdown() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: "easeOut"
      }
    }
  };
  return <section id="supplierpagebreakdown" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="page-container">
        {/* Header Section */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <Badge variant="secondary" className="mb-4 text-xs font-semibold px-3 py-1">
            Standard Partner Structure
          </Badge>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold tracking-tight text-foreground">
            What Every Partner Page Delivers
          </h2>
          <p className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed">
            Every manufacturer portfolio hosted by Antech Solutions follows a rigorous 5-pillar structure, giving regulated laboratories full visibility across technical capability, compliant hardware, and local lifecycle services.
          </p>
        </div>

        {/* 5-Column / Grid Breakdown */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sections.map((item, index) => {
          const IconComponent = item.icon;
          const isFeatured = index === 4; // Antech Services highlight

          return <motion.div key={item.title} data-index={index} variants={cardVariants} className={`h-full ${isFeatured ? "md:col-span-2 lg:col-span-2" : "col-span-1"}`}>
                <Card className={`h-full border transition-all duration-300 relative overflow-hidden ${isFeatured ? "bg-secondary/40 border-primary/30 shadow-md" : "bg-card border-border shadow-xs hover:border-primary/40 hover:shadow-sm"}`}>
                  {isFeatured && <div className="absolute top-0 right-0 left-0 h-1 bg-primary" />}

                  <CardHeader className="space-y-3 pb-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`size-10 rounded-md flex items-center justify-center ${isFeatured ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground"}`}>
                          <IconComponent className="size-5" />
                        </div>
                        <span className="text-xs font-mono font-semibold text-muted-foreground">
                          STAGE {item.number}
                        </span>
                      </div>
                      <Badge variant={isFeatured ? "default" : "outline"} className="text-[11px]">
                        {item.badge}
                      </Badge>
                    </div>

                    <CardTitle className="text-xl md:text-2xl font-bold font-default text-foreground">
                      {item.title}
                    </CardTitle>
                    <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                      {item.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="pt-2">
                    <div className="border-t border-border/60 pt-4">
                      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                        Key Deliverables
                      </p>
                      <ul className="space-y-2.5">
                        {item.keyPoints.map((point, pIndex) => <li key={pIndex} data-index={pIndex} className="flex items-start gap-2.5 text-xs md:text-sm text-foreground/90">
                            <CheckCircle2 className={`size-4 shrink-0 mt-0.5 ${isFeatured ? "text-primary" : "text-chart-2"}`} />
                            <span>{point}</span>
                          </li>)}
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Closing Action Banner */}
        <div className="mt-12 md:mt-16 bg-card border border-border rounded-xl p-6 md:p-8 shadow-xs flex flex-col md:flex-row items-start md:items-center justify-between gap-6 relative overflow-hidden">
          <div className="space-y-1.5 max-w-xl">
            <div className="inline-flex items-center gap-1.5 text-primary text-xs font-semibold uppercase tracking-wider">
              <Sparkles className="size-3.5" />
              <span>Tailored Technical Consultations</span>
            </div>
            <h3 className="text-xl md:text-2xl font-bold text-foreground font-default">
              Discuss your application with an Antech specialist
            </h3>
            <p className="text-sm text-muted-foreground">
              Get direct technical guidance, instrument recommendations, and qualification scopes suited to your compliance requirements.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full md:w-auto shrink-0">
            <Button asChild size="lg" className="bg-foreground text-background hover:bg-foreground/90 border-l-4 border-primary font-semibold px-6 shadow-sm">
              <Link to="/ContactPage">
                Contact Us
                <ArrowRight className="size-4 ml-1.5 text-primary" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}